import type { iconTypes } from '../components/ui/Icon'

export type FieldSectionRef = {
  handle: string
  name: string
  /** Lucide icon shown next to the section name. */
  icon?: iconTypes | null
  /** Logo shown next to the section name. Takes precedence over `icon`. */
  iconUrl?: string | null
  /** Extra tooltip lines that tell similar sections apart. */
  details?: string[]
  /** Menus that collapse sections start this one expanded. */
  defaultOpen?: boolean
}

export type FieldWithOptionalSection = {
  section?: FieldSectionRef | null
}

export type GroupedFieldsBySection<T extends FieldWithOptionalSection> = {
  unsectioned: T[]
  sections: Array<FieldSectionRef & { fields: T[] }>
}

/**
 * Split a flat field list into unsectioned fields + ordered sections.
 * Section order follows first appearance in the input list.
 */
export function groupFieldsBySection<T extends FieldWithOptionalSection>(
  fields: T[],
): GroupedFieldsBySection<T> {
  const unsectioned: T[] = []
  const sections: GroupedFieldsBySection<T>['sections'] = []
  const sectionIndexByHandle = new Map<string, number>()

  for (const field of fields) {
    const section = field.section
    if (!section?.handle) {
      unsectioned.push(field)
      continue
    }

    const existingIndex = sectionIndexByHandle.get(section.handle)
    if (existingIndex === undefined) {
      sectionIndexByHandle.set(section.handle, sections.length)
      sections.push({
        ...section,
        name: section.name || section.handle,
        fields: [field],
      })
      continue
    }

    sections[existingIndex]?.fields.push(field)
  }

  return { unsectioned, sections }
}
